import { useMemo } from 'react';
import { InterfaceResourceStrategies } from '../../../../../context/InterfaceResourceStrategyProvider/constants';
import { useDeployedInterfaceDetails } from '../../../../../context/PublishedInterfaceProvider/PublishedInterfaceProvider';
import { useSavedInterfaceVersionDetails } from '../../../../../context/SavedInterfaceVersionDetailsProvider/SavedInterfaceVersionDetailsProvider';

interface UseWalkthroughParamsOptions {
  interfacePageId?: string;
  interfaceId?: string;
  strategy: string;
  device?: string;
}

function useWalkthroughParamsBase({
  interfacePageId,
  interfaceId,
  strategy,
  device,
}: UseWalkthroughParamsOptions) {
  return useMemo(
    () => ({
      pageId: interfacePageId,
      interfaceId,
      strategy,
      device,
    }),
    [interfacePageId, interfaceId, strategy, device],
  );
}

function useWalkthroughParamsDeployedByVersion(options: UseWalkthroughParamsOptions) {
  const base = useWalkthroughParamsBase(options);
  const { deployedVersion } = useDeployedInterfaceDetails();
  return useMemo(() => ({ ...base, deployedVersion }), [base, deployedVersion]);
}

function useWalkthroughParamsSavedByVersion(options: UseWalkthroughParamsOptions) {
  const base = useWalkthroughParamsBase(options);
  const { currentVersion } = useSavedInterfaceVersionDetails();
  return useMemo(() => ({ ...base, currentVersion }), [base, currentVersion]);
}

// Strategy variants that need an extra context subscription. Strategies absent
// from this map fall back to the base shape. Strategy must remain stable per
// component lifetime — same Rules-of-Hooks contract the previous per-file
// dispatch implementation already relied on.
const STRATEGY_HOOK_OVERRIDES: Record<
  string,
  (options: UseWalkthroughParamsOptions) => Record<string, unknown>
> = {
  [InterfaceResourceStrategies.DeployedByVersion]: useWalkthroughParamsDeployedByVersion,
  [InterfaceResourceStrategies.SavedByVersion]: useWalkthroughParamsSavedByVersion,
};

function useWalkthroughParameters(options: UseWalkthroughParamsOptions) {
  const useParamsHook = STRATEGY_HOOK_OVERRIDES[options.strategy] ?? useWalkthroughParamsBase;
  return useParamsHook(options);
}

export default useWalkthroughParameters;
